import { useEffect, useRef, useState } from "react";
import type { Destination } from "../lib/types";
import type { RenderResult } from "../lib/shareCard";

interface Props {
  destination: Destination;
  sessionId: string;
  onClose: () => void;
}

type Status = "rendering" | "ready" | "error";

export function ShareCardModal({ destination, sessionId, onClose }: Props) {
  const [status, setStatus] = useState<Status>("rendering");
  const [result, setResult] = useState<RenderResult | null>(null);
  const [errMsg, setErrMsg] = useState<string>("");
  const [canShareFile, setCanShareFile] = useState(false);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    // Lazy-load the canvas renderer (TDD §3.1)
    import("../lib/shareCard")
      .then(async ({ renderShareCard }) => {
        const r = await renderShareCard({ destination, sessionId });
        if (cancelled.current) {
          URL.revokeObjectURL(r.url);
          return;
        }
        setResult(r);
        setStatus("ready");

        const file = new File([r.blob], filenameFor(destination, r.mime), { type: r.mime });
        if (typeof navigator !== "undefined" && "canShare" in navigator) {
          try {
            setCanShareFile(navigator.canShare!({ files: [file] }));
          } catch {
            setCanShareFile(false);
          }
        }
      })
      .catch((err) => {
        if (cancelled.current) return;
        setErrMsg(err?.message ?? "渲染失败");
        setStatus("error");
      });

    return () => {
      cancelled.current = true;
    };
  }, [destination, sessionId]);

  // Revoke the object URL on unmount (separate effect — must read latest result.url).
  useEffect(() => {
    return () => {
      if (result?.url) URL.revokeObjectURL(result.url);
    };
  }, [result]);

  const onShare = async () => {
    if (!result) return;
    const file = new File([result.blob], filenameFor(destination, result.mime), { type: result.mime });
    try {
      await navigator.share({
        files: [file],
        title: `${destination.name_zh} · 我的下一片海`,
        text: destination.oneliner_zh ?? `${destination.name_zh} · 去哪潜`,
      });
    } catch {
      // User cancelled or share failed; nothing to do.
    }
  };

  return (
    <div className="modal-backdrop active" onClick={onClose}>
      <div className="modal-sheet share-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="modal-handle" />

        <div className="share-title">分享你的结果</div>
        <div className="share-hint">长按图片即可保存到相册或转发给朋友</div>

        <div className="share-preview">
          {status === "rendering" && (
            <div className="share-preview-placeholder">
              <div className="share-spinner" aria-hidden />
              <div className="share-loading-text">正在生成卡片…</div>
            </div>
          )}
          {status === "ready" && result && (
            <img
              className="share-preview-img"
              src={result.url}
              alt={`${destination.name_zh} 分享卡片`}
              draggable={false}
            />
          )}
          {status === "error" && (
            <div className="share-preview-placeholder">
              <div className="share-error">没生成出来：{errMsg}</div>
            </div>
          )}
        </div>

        <div className="share-actions">
          {canShareFile && status === "ready" && (
            <button className="btn btn-primary" onClick={onShare}>分享…</button>
          )}
          {status === "ready" && result && (
            <a
              className={"btn " + (canShareFile ? "btn-ghost" : "btn-primary")}
              href={result.url}
              download={filenameFor(destination, result.mime)}
            >
              保存到相册
            </a>
          )}
          <button className="btn btn-text" onClick={onClose}>关闭</button>
        </div>
      </div>
    </div>
  );
}

function filenameFor(d: Destination, mime: string): string {
  const ext = mime === "image/jpeg" ? "jpg" : "png";
  return `dive-where-${d.id}.${ext}`;
}
